<template>
  <v-container fluid class="pa-6 pt-3">
    <v-card class="mb-5 " elevation="0">
      <v-card-text class="pa-3" style="padding-left:0px !important" >
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model.trim="form.usn_id"
              label="USN"
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="search"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model.trim="form.application_id"
              label="Application ID"
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="search"
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-autocomplete
              v-model="form.fee_types"
              :items="feeTypes"
              item-text="text"
              item-value="id"
              label="Fee Type"
              outlined
              dense
              clearable
              multiple
              chips
              small-chips
              deletable-chips
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.term_no"
              :items="termOptions"
              label="Payment Term"
              outlined
              dense
              clearable
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="displayDate"
                  label="Payment Date"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  hide-details="auto"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="resetDates"
                />
              </template>
              <v-date-picker
                v-model="selectedDates"
                range
                @input="onDateChange"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.source_type"
              :items="sourceOptions"
              item-text="text"
              item-value="value"
              label="Source"
              outlined
              dense
              clearable
              multiple
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="form.refund_status"
              :items="refundStatusOptions"
              item-text="text"
              item-value="value"
              label="Refund Status"
              outlined
              dense
              clearable
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <v-btn
              class="accent white--text mr-1"
              :loading="loading"
              :disabled="loading"
              @click="search"
            >
              Search
            </v-btn>
            <v-btn color="accent white--text" @click="goToRefunded()">
              Refunds
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row class="mb-2">
      <v-col v-for="tile in summaryTiles" :key="tile.label" class="py-0" cols="6" md="3">
        <v-sheet outlined rounded class="pa-3 text-center">
          <div class="caption grey--text text--darken-1">{{ tile.label }}</div>
          <div class="subtitle-1 font-weight-bold" :class="tile.class">
            {{ tile.value }}
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-sheet outlined>
      <v-data-table
        :headers="refundStudentHeaders"
        :items="groups"
        :loading="loading"
        :expanded.sync="expanded"
        item-key="student_key"
        show-expand
        single-expand
        fixed-header
      >
        <template v-slot:no-data>
          <div class="text-center py-4 grey--text">No paid payments found</div>
        </template>

        <template #[`item.name`]="{ item }">
          {{ item.name || "—" }}
        </template>

        <template #[`item.usn_id`]="{ item }">
          {{ item.usn_id || "—" }}
        </template>

        <template #[`item.application_id`]="{ item }">
          {{ item.application_id || "—" }}
        </template>

        <template #[`item.mobile_no`]="{ item }">
          {{ item.mobile_no || "—" }}
        </template>

        <template #[`item.tally.payments_count`]="{ item }">
          <v-chip x-small outlined color="primary">
            {{ item.tally.payments_count }}
          </v-chip>
        </template>

        <template #[`item.tally.paid`]="{ item }">
          {{ money(item.tally.paid) }}
        </template>

        <template #[`item.tally.refunded`]="{ item }">
          <span
            :class="
              toNumber(item.tally.refunded) > 0
                ? 'orange--text text--darken-2 font-weight-bold'
                : 'grey--text'
            "
          >
            {{ money(item.tally.refunded) }}
          </span>
        </template>

        <template #[`item.tally.net`]="{ item }">
          <span class="font-weight-bold">{{ money(item.tally.net) }}</span>
        </template>

        <template #[`item.actions`]="{ item, isExpanded, expand }">
          <div class="d-flex justify-end" style="gap: 6px">
            <v-btn
              small
              outlined
              color="primary darken-1"
              :disabled="item.tally.refunded_count === 0"
              @click="goToRefunded(item)"
            >
              Refunds
            </v-btn>
          </div>
        </template>


        <template #expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0 grey lighten-4">
            <div class="px-4 py-3">
              <div class="d-flex align-center mb-2">
                <v-icon small color="grey darken-1" class="mr-2">
                  mdi-receipt-text-outline
                </v-icon>
                <span class="subtitle-2 grey--text text--darken-3">
                  Payments
                </span>
                <v-chip x-small class="ml-2" outlined color="primary">
                  {{ item.payments.length }}
                </v-chip>
              </div>

              <v-sheet
                v-for="payment in item.payments"
                :key="payment.id"
                outlined
                rounded
                class="px-3 py-2 mb-2"
              >
                <v-row align="center" no-gutters>
                  <v-col cols="12" md="10">
                    <v-row dense>
                      <v-col
                        v-for="field in paymentFields(payment)"
                        :key="field.label"
                        cols="6"
                        sm="4"
                        md="3"
                        class="py-1 pr-3"
                      >
                        <div class="caption grey--text text--darken-1" style="line-height: 1.2">
                          {{ field.label }}
                        </div>
                        <div class="body-2 font-weight-medium text-truncate">
                          {{ field.value }}
                        </div>
                      </v-col>

                      <v-col cols="6" sm="4" md="3" class="py-1 pr-3">
                        <div class="caption grey--text text--darken-1" style="line-height: 1.2">
                          Refund
                        </div>
                        <v-chip
                          x-small
                          :color="
                            payment.refund ? 'orange darken-2' : 'grey lighten-1'
                          "
                          dark
                        >
                          {{ payment.refund ? "Refunded" : "Not Refunded" }}
                        </v-chip>
                        <div v-if="payment.refund" class="caption grey--text mt-1">
                          {{ payment.refund.refunded_date }} ·
                          {{ payment.refund.refund_transaction_id }}
                        </div>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col cols="12" md="2" class="d-flex justify-md-end pt-1 pt-md-0">
                    <v-btn
                      small
                      :outlined="!!payment.refund"
                      :color="payment.refund ? 'grey' : 'primary darken-1'"
                      :dark="!payment.refund"
                      :disabled="!!payment.refund"
                      @click="openRefundDialog(payment)"
                    >
                      <v-icon left x-small>mdi-cash-refund</v-icon>
                      Refund
                    </v-btn>
                  </v-col>
                </v-row>
              </v-sheet>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-sheet>

    <v-dialog v-model="refundDialog" max-width="900" persistent scrollable>
      <refund-dialog
        v-if="refundDialog"
        :payment="selectedPayment"
        @close="refundDialog = false"
        @saved="onRefundSaved"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import refundDialog from "@/components/refund/refundDialog.vue";
import { refundStudentHeaders } from "@/constants/headers";

export default {
  components: { refundDialog },

  data() {
    return {
      refundStudentHeaders,
      loading: false,
      dateMenu: false,
      selectedDates: [],
      expanded: [],
      groups: [],
      refundDialog: false,
      selectedPayment: null,
      form: {
        usn_id: "",
        application_id: "",
        source_type: [],
        fee_types: [],
        term_no: null,
        refund_status: null,
        from_date: "",
        to_date: "",
      },
      feeTypes: [
        { id: "Term Fee", text: "Term Fee" },
        { id: "Annual", text: "Annual" },
        { id: "Full", text: "Full" },
        { id: "Advance Tuition Fee", text: "Advance Tuition Fee" },
        { id: "Exam Fee", text: "Exam Fee" },
        { id: "Re-Admission Fee", text: "Re-Admission Fee" },
        { id: "Convocation Fee", text: "Convocation Fee" },
        { id: "Postal Fee", text: "Postal Fee" },
        { id: "Add-on Programme", text: "Add-on Programme" },
      ],
      sourceOptions: [
        { text: "Academic", value: "student_invoice" },
        { text: "Admission", value: "student_admit_plan_payment" },
        { text: "Pre-Application", value: "pre_application_fee_payment" },
      ],
      termOptions: [1, 2, 3, 4, 5, 6],
      refundStatusOptions: [
        { text: "Refunded", value: "refunded" },
        { text: "Not Refunded", value: "not_refunded" },
      ],
    };
  },

  computed: {
    totalPayments() {
      return this.groups.reduce(
        (total, group) => total + group.tally.payments_count,
        0,
      );
    },

    displayDate() {
      if (!this.form.from_date) return "";
      if (!this.form.to_date || this.form.to_date === this.form.from_date) {
        return this.form.from_date;
      }
      return `${this.form.from_date} → ${this.form.to_date}`;
    },

    summaryTiles() {
      const paid = this.sum("paid");
      const refunded = this.sum("refunded");

      return [
        { label: "Students", value: this.groups.length },
        { label: "Payments", value: this.totalPayments },
        {
          label: "Total Paid",
          value: this.money(paid),
          class: "accent--text",
        },
        {
          label: "Total Refunded",
          value: this.money(refunded),
          class: refunded > 0 ? "orange--text text--darken-2" : "grey--text",
        },
      ];
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Refunds" }]);
    this.fetchRefundableInvoices();
  },

  methods: {
    toNumber(value) {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    },

    money(value) {
      return `₹${this.toNumber(value).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      })}`;
    },

    paymentFields(payment) {
      return [
        { label: "Date", value: payment.transaction_date || "—" },
        { label: "Fee Type", value: payment.fee_type || "—" },
        { label: "Source", value: payment.source_label || "—" },
        { label: "Payment Term", value: payment.term_no || "—" },
        { label: "Amount", value: this.money(payment.amount) },
        { label: "Receipt No", value: payment.receipt_number || "—" },
        { label: "Transaction ID", value: payment.transaction_id || "—" },
      ];
    },

    sum(key) {
      return this.groups.reduce(
        (total, group) => total + this.toNumber(group.tally[key]),
        0,
      );
    },

    onDateChange() {
      if (!this.selectedDates.length) return;
      const [start, end] = [...this.selectedDates].sort();
      this.form.from_date = start;
      this.form.to_date = end || start;
      if (this.selectedDates.length === 2) this.dateMenu = false;
    },

    resetDates() {
      this.selectedDates = [];
      this.form.from_date = "";
      this.form.to_date = "";
    },

    search() {
      this.expanded = [];
      this.fetchRefundableInvoices();
    },

    buildQuery() {
      const params = {
        ...this.form,
        fee_types: this.form.fee_types.join(","),
        source_type: this.form.source_type.join(","),
      };

      return Object.keys(params)
        .filter(
          (key) =>
            params[key] !== undefined &&
            params[key] !== null &&
            params[key] !== "",
        )
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`,
        )
        .join("&");
    },

    async fetchRefundableInvoices() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          `/refundable_payments?${this.buildQuery()}`,
        );
        this.groups = res.data.data || [];
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Failed to load payments",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    openRefundDialog(payment) {
      this.selectedPayment = payment;
      this.refundDialog = true;
    },

    onRefundSaved() {
      this.refundDialog = false;
      const openKey = this.expanded.length
        ? this.expanded[0].student_key
        : null;

      this.fetchRefundableInvoices().then(() => {
        if (!openKey) return;
        const refreshed = this.groups.find((g) => g.student_key === openKey);
        this.expanded = refreshed ? [refreshed] : [];
      });
    },

    goToRefunded(group) {
      this.$router.push({
        name: "Refunded",
        query: group ? { usn_id: group.usn_id || group.application_id } : {},
      });
    },
  },
};
</script>
